/*!
 * Copyright (c) REWE Digital GmbH
 */
import { Host, h, Build } from "@stencil/core";
import { Subject } from "rxjs";
import { debounceTime } from "rxjs/operators";
import { getAnchorElementByEvent, getSubmitButtonByEvent, isFirefox, isOutsideEvent, keyValueStore } from "@rdss/core";
/**
 * `VxGlobalSearch` is a component that renders a global search form.
 *
 * @example
 * <vx-global-search searchService={AwesomeSearchService} suggestionsTemplate="wf-suggestions-template" />
 *
 * @part form - The search form itself
 * @part toggle-button - The button used to toggle the search form
 * @part close-button - The button used to close the search form
 * @part clear-button - The button used to clear the input field
 * @part input - The input field in the search form
 * @part suffix-icon - The search icon in the search form
 * @part suggestions - The suggestions container
 * @part link - The link for each simple suggestion item.
 * @part submit-button - The button to submit the form.
 */
// eslint-disable-next-line require-jsdoc
export class VxGlobalSearch {
    constructor() {
        var _a;
        this.suggestionsTemplateElements = {};
        this.inputSubject = new Subject();
        /**
         * Sets the active suggestion anchor.
         */
        this.setActiveSuggestionAnchor = () => {
            var _a;
            const anchors = this.suggestionsTemplateElements.anchors;
            anchors === null || anchors === void 0 ? void 0 : anchors.forEach(anchor => anchor.classList.remove('active'));
            (_a = anchors === null || anchors === void 0 ? void 0 : anchors.find(anchor => anchor.pathname === location.pathname)) === null || _a === void 0 ? void 0 : _a.classList.add('active');
        };
        /**
         * Handles focus on search input.
         */
        this.handleInputFocus = () => {
            var _a;
            this.inputElement.value = this.searchTerm;
            (_a = this.inputElement) === null || _a === void 0 ? void 0 : _a.select();
        };
        /**
         * Handles the form submit.
         */
        this.handleFormSubmit = (e) => {
            if (this.searchTerm.length === 0 || !this.hasSearchResults) {
                return e.preventDefault();
            }
            this.closeSearch();
        };
        /**
         * Sets up a debounced search listener that triggers the search when the input value changes and is long enough.
         */
        this.setupDebouncedSearch = () => {
            this.inputSubscription = this.inputSubject
                .pipe(debounceTime(this.debounceTime))
                .subscribe(async (inputValue) => {
                if (inputValue.length && (inputValue.length >= this.searchTermMinLength)) {
                    this.isLoading = true;
                    this.searchResults = await this.search(inputValue);
                    this.isDisplaySuggestions = true;
                    this.isLoading = false;
                }
                else {
                    this.isDisplaySuggestions = false;
                }
            });
        };
        /**
         * Dispatches a custom event named by the `searchChangedEventName` prop.
         * The detail of the event contains the current search results.
         */
        this.dispatchSearchChangedEvent = () => {
            const searchChangedEvent = new CustomEvent(this.searchChangedEventName, { detail: this.searchResults });
            this.suggestionsTemplateElement.dispatchEvent(searchChangedEvent);
        };
        /**
         * Sets the search-term by localstorage.
         */
        this.setSearchTermFromStorage = async () => {
            var _a;
            if (this.storeSearchTerm && Build.isBrowser) {
                this.searchTerm = (_a = await keyValueStore.get(this.searchTermStorageKey)) !== null && _a !== void 0 ? _a : '';
            }
        };
        /**
         * Performs a search using the search service and returns the results.
         */
        this.search = async (inputValue) => this.searchService.findSuggestions(inputValue);
        /**
         * Focuses the input field in the search form.
         */
        this.focusInput = () => this.inputElement.focus();
        /**
         * Handles the input change event and updates the search term state.
         */
        this.handleInputChange = ({ target }) => {
            this.searchTerm = (target.value);
        };
        /**
         * Submits the search form.
         */
        this.submitForm = () => this.formElement.submit();
        /**
         * Blurs the host element, effectively closing the search form.
         */
        this.closeSearch = () => {
            if (isFirefox())
                return document.documentElement.focus();
            this.host.blur();
        };
        /**
         * Clears the input field in the search form.
         * If the input field is already empty, the search form is closed.
         */
        this.clearOrClose = () => {
            if (this.searchTerm.length === 0)
                return this.closeSearch();
            this.isDisplaySuggestions = false;
            this.searchTerm = '';
            this.focusInput();
        };
        this.searchService = undefined;
        this.suggestionsTemplate = undefined;
        this.initialWidth = '200px';
        this.focusedWidth = '800px';
        this.placeholder = undefined;
        this.searchTerm = '';
        this.searchIcon = 'search';
        this.closeIcon = 'arrow_back';
        this.clearIcon = 'close';
        this.debounceTime = 300;
        this.searchTermMinLength = 2;
        this.formAction = '/search';
        this.inputName = 'sSearch';
        this.searchChangedEventName = 'globalSearchChanged';
        this.storeSearchTerm = false;
        this.searchTermStorageKey = 'global-search-search-term';
        this.snippets = {
            searchFieldLabel: 'Suchfeld',
            toggleButton: 'Suche anzeigen',
            inputPlaceholder: (_a = this.placeholder) !== null && _a !== void 0 ? _a : 'Suchen',
            closeButtonLabel: 'Schließen',
            clearButtonLabel: 'Löschen',
        };
        this.compact = false;
        this.isLoading = false;
        this.hasSearchResults = undefined;
        this.searchResults = undefined;
        this.isDisplaySuggestions = false;
    }
    /**
     * Sets up the debounced search listener when the component is connected.
     */
    connectedCallback() {
        this.setupDebouncedSearch();
    }
    /**
     * @inheritdoc
     */
    componentDidRender() {
        this.assignSuggestionsTemplateElements();
        this.setActiveSuggestionAnchor();
    }
    /**
     * @inheritdoc
     */
    async componentDidLoad() {
        this.assignCssProperties();
        await this.setSearchTermFromStorage();
    }
    /**
     * @inheritdoc
     */
    disconnectedCallback() {
        var _a;
        (_a = this.inputSubscription) === null || _a === void 0 ? void 0 : _a.unsubscribe();
    }
    /**
     * Handles the window resize event.
     * This method is called whenever the window is resized.
     * It updates the CSS properties of the host element.
     */
    handleResize() {
        this.assignCssProperties();
    }
    /**
     * Handles the click event on the component.
     * If the event was triggered by an anchor element, it closes the search.
     * If the event was triggered by a submit button, it submits the form.
     */
    handleClickDeep(e) {
        if (getAnchorElementByEvent(e))
            return this.closeSearch();
        if (getSubmitButtonByEvent(e))
            return this.submitForm();
    }
    /**
     * Handles the touchstart event on the document and triggers the closing of the search form.
     */
    handleTouchStart(e) {
        if (isOutsideEvent(e, this.host))
            this.closeSearch();
    }
    /**
     * Handles the keydown event. It provides navigation through the search results using the arrow keys.
     */
    handleKeyDown(e) {
        var _a, _b, _c, _d;
        const navigationKeys = ['Escape', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Enter', 'Tab'];
        const { sections, anchors } = this.suggestionsTemplateElements;
        const navigableAnchors = anchors.filter(a => a.pathname !== location.pathname);
        const focusedElement = e.composedPath().shift();
        const focusedAnchorIndex = navigableAnchors.indexOf(focusedElement);
        const focusedContainer = focusedElement.closest('[part="suggestions-section"]');
        const focusedContainerIndex = sections.findIndex(container => container === focusedContainer);
        const lastContainer = sections[sections.length - 1];
        const firstContainer = sections[0];
        const nextContainer = (_a = sections[focusedContainerIndex + 1]) !== null && _a !== void 0 ? _a : firstContainer;
        const previousContainer = (_b = sections[focusedContainerIndex - 1]) !== null && _b !== void 0 ? _b : lastContainer;
        const firstAnchor = navigableAnchors[0];
        const lastAnchor = navigableAnchors[navigableAnchors.length - 1];
        const nextAnchor = (_c = navigableAnchors[focusedAnchorIndex + 1]) !== null && _c !== void 0 ? _c : firstAnchor;
        const previousAnchor = (_d = navigableAnchors[focusedAnchorIndex - 1]) !== null && _d !== void 0 ? _d : lastAnchor;
        const inputFocused = focusedElement.localName === 'input';
        /**
         * Navigates to the previous container.
         * It focuses the first anchor element of the previous container that is not active.
         */
        const navigateLeft = () => { var _a; return (_a = previousContainer === null || previousContainer === void 0 ? void 0 : previousContainer.querySelector('a:not(.active)')) === null || _a === void 0 ? void 0 : _a.focus(); };
        /**
         * Navigates to the next container.
         * It focuses the first anchor element of the next container that is not active.
         */
        const navigateRight = () => { var _a; return (_a = nextContainer === null || nextContainer === void 0 ? void 0 : nextContainer.querySelector('a:not(.active)')) === null || _a === void 0 ? void 0 : _a.focus(); };
        /**
         * Navigates to the previous anchor.
         * It focuses the previous anchor element in the list of navigable anchors.
         */
        const navigateUp = () => previousAnchor === null || previousAnchor === void 0 ? void 0 : previousAnchor.focus();
        /**
         * Navigates to the next anchor.
         * If the input field is focused, it focuses the first anchor element.
         * Otherwise, it focuses the next anchor element in the list of navigable anchors.
         */
        const navigateDown = () => {
            if (inputFocused)
                return firstAnchor === null || firstAnchor === void 0 ? void 0 : firstAnchor.focus();
            nextAnchor === null || nextAnchor === void 0 ? void 0 : nextAnchor.focus();
        };
        /**
         * Focuses the input field and positions the cursor at the end of the input value.
         */
        const focusAndPositionCursor = () => {
            this.focusInput();
            this.inputElement.selectionStart = this.inputElement.value.length;
        };
        if (!inputFocused && !navigationKeys.includes(e.key)) {
            return focusAndPositionCursor();
        }
        switch (e.key) {
            case 'ArrowLeft':
                return !inputFocused && navigateLeft();
            case 'ArrowRight':
                return !inputFocused && navigateRight();
            case 'ArrowUp':
                e.preventDefault();
                return !inputFocused && navigateUp();
            case 'ArrowDown':
                e.preventDefault();
                return navigateDown();
            case 'Escape':
                return this.clearOrClose();
        }
    }
    /**
     * Watches the search results state.
     */
    handleSearchResults(results) {
        this.hasSearchResults = Object
            .values(results)
            .filter(value => Array.isArray(value))
            .flat()
            .length > 0;
        this.searchResults.hasResults = this.hasSearchResults;
        this.dispatchSearchChangedEvent();
    }
    /**
     * Watches the search term state.
     */
    async handleSearchTermChange(term) {
        if (this.storeSearchTerm) {
            await keyValueStore.set(this.searchTermStorageKey, term !== null && term !== void 0 ? term : '');
        }
        this.inputSubject.next(term);
    }
    /**
     * @inheritdoc
     */
    render() {
        var _a;
        const Suggestions = this.suggestionsTemplate;
        return (h(Host, { key: '80321486d6f9c70e7c8897ec6c71db0bfc150e70' }, h("div", { key: 'eb94ffbe719c483b982491740f14e244711a6cbf', part: "wrapper", class: { 'has--search-results': this.hasSearchResults, 'is--compact': this.compact }, role: "navigation", "aria-label": this.snippets.searchFieldLabel }, h("vx-icon-button", { key: '0c42ad6d9557dd46b08e90a0a83ab16e00a19e88', onClick: this.focusInput, part: "toggle-button", "aria-label": this.snippets.toggleButton, icon: this.searchIcon }), h("form", { key: '681bc2ea034f8aebe954b32af3e58198375876c8', onSubmit: this.handleFormSubmit, ref: el => (this.formElement = el), role: "search", part: "form", action: this.formAction, method: "get" }, h("vx-icon-button", { key: '8a2c130039a0b42c15df5a2477c20e6c805472c3', icon: this.closeIcon, part: "close-clear-button", "aria-label": this.snippets.closeButtonLabel, onClick: this.closeSearch }), h("input", { key: 'e07df812b9a19d5c375478a3c1835dc3ac0fe427', onFocus: this.handleInputFocus, name: this.inputName, onInput: this.handleInputChange, autocomplete: "off", ref: el => (this.inputElement = el), part: "input", type: "search", placeholder: this.snippets.inputPlaceholder, "aria-label": this.snippets.searchFieldLabel }), h("vx-icon", { key: '741ab79ec26fd4bd245c579177aedb08c6bf6aa2', icon: "search", part: "suffix-icon", "aria-hidden": "true" }), (((_a = this.searchTerm) === null || _a === void 0 ? void 0 : _a.length) > 0 || this.compact) && (h("vx-icon-button", { icon: this.clearIcon, part: "clear-button", "aria-label": this.snippets.clearButtonLabel, onClick: this.clearOrClose, "no-ripple": true })), !this.compact &&
            h("vx-button", { onClick: this.closeSearch, part: "close-button", label: this.snippets.closeButtonLabel })), h("div", { key: '66a17d751d5fb0e54f089e425303e5b14db09aaa', class: {
                'is--loading': this.isLoading,
                'is--open': this.isDisplaySuggestions,
            }, part: "suggestions", ref: el => (this.suggestionsContainerElement = el), "aria-live": "polite", tabindex: "-1", "aria-busy": this.isLoading.toString() }, h(Suggestions, { key: 'fe30f129412c0b341feee4e157cfc231b8f01a8d', ref: (el) => (this.suggestionsTemplateElement = el) })))));
    }
    /**
     * Sets the elements of the suggestions template.
     * It assigns the anchor elements and sections of the suggestions template to the `suggestionsTemplateElements` state.
     */
    assignSuggestionsTemplateElements() {
        const templateRoot = this.suggestionsTemplateElement.shadowRoot || this.suggestionsTemplateElement;
        this.suggestionsTemplateElements = {
            anchors: Array.from(templateRoot.querySelectorAll('a')),
            sections: Array.from(templateRoot.querySelectorAll('[part="suggestions-section"]')),
        };
    }
    /**
     * Assigns CSS properties to the host element.
     * It sets the initial and expanded width of the search form and the top value of the mobile suggestions.
     */
    assignCssProperties() {
        var _a, _b;
        const hostStyle = this.host.style;
        hostStyle.setProperty('--vx-global-search-initial-width', this.initialWidth);
        hostStyle.setProperty('--vx-global-search-expanded-width', this.focusedWidth || this.initialWidth);
        hostStyle.setProperty('--mobile-suggestions-top-value', (_b = (_a = getComputedStyle(this.suggestionsContainerElement)) === null || _a === void 0 ? void 0 : _a.top) !== null && _b !== void 0 ? _b : '0');
    }
    static get is() { return "vx-global-search"; }
    static get encapsulation() { return "shadow"; }
    static get originalStyleUrls() {
        return {
            "$": ["vx-global-search.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["vx-global-search.css"]
        };
    }
    static get properties() {
        return {
            "searchService": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "SearchService<Suggestions>",
                    "resolved": "SearchService<Suggestions>",
                    "references": {
                        "SearchService": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/vx-global-search/types/index.ts::SearchService"
                        },
                        "Suggestions": {
                            "location": "global",
                            "id": "global::Suggestions"
                        }
                    }
                },
                "required": true,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The search service to be used for finding suggestions."
                }
            },
            "suggestionsTemplate": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": true,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The component to be used for rendering suggestions."
                },
                "attribute": "suggestions-template",
                "reflect": false
            },
            "initialWidth": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "The initial width of the search form. This is applied when the search form is not focused.\nIt is a CSS property and can accept any valid CSS width value. Default is \"200px\"."
                },
                "attribute": "initial-width",
                "reflect": false,
                "defaultValue": "'200px'"
            },
            "focusedWidth": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "The expanded width of the search form. This is applied when the search form is focused.\nIf not provided, the initial width will be used. It is a CSS property and can accept any valid CSS width value.\nDefault is \"800px\"."
                },
                "attribute": "focused-width",
                "reflect": false,
                "defaultValue": "'800px'"
            },
            "placeholder": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "The placeholder of the input element."
                },
                "attribute": "placeholder",
                "reflect": false
            },
            "searchTerm": {
                "type": "string",
                "mutable": true,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The current search term."
                },
                "attribute": "search-term",
                "reflect": false,
                "defaultValue": "''"
            },
            "searchIcon": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The icon to be displayed in the search form as a suffix. Default is 'search'."
                },
                "attribute": "search-icon",
                "reflect": false,
                "defaultValue": "'search'"
            },
            "closeIcon": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The icon to be displayed in the close button. Default is 'arrow_back'."
                },
                "attribute": "close-icon",
                "reflect": false,
                "defaultValue": "'arrow_back'"
            },
            "clearIcon": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The icon to be displayed in the clear-button. Default is 'close'."
                },
                "attribute": "clear-icon",
                "reflect": false,
                "defaultValue": "'close'"
            },
            "debounceTime": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The debounce time for the search input. Default is 300ms."
                },
                "attribute": "debounce-time",
                "reflect": false,
                "defaultValue": "300"
            },
            "searchTermMinLength": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The minimum length of the search term to start finding suggestions. Default is 2."
                },
                "attribute": "search-term-min-length",
                "reflect": false,
                "defaultValue": "2"
            },
            "formAction": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The action URL for the search form. Default is '/search'."
                },
                "attribute": "form-action",
                "reflect": false,
                "defaultValue": "'/search'"
            },
            "inputName": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The name of the search input field. Default is 'sSearch'."
                },
                "attribute": "input-name",
                "reflect": false,
                "defaultValue": "'sSearch'"
            },
            "searchChangedEventName": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The name of the search-changed event."
                },
                "attribute": "search-changed-event-name",
                "reflect": false,
                "defaultValue": "'globalSearchChanged'"
            },
            "storeSearchTerm": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "If true the search term will be stored in indexedDB to resume the search session for the user."
                },
                "attribute": "store-search-term",
                "reflect": false,
                "defaultValue": "false"
            },
            "searchTermStorageKey": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The key under which the search term is stored in indexedDB storage.\nDefault is 'global-search-search-term'."
                },
                "attribute": "search-term-storage-key",
                "reflect": false,
                "defaultValue": "'global-search-search-term'"
            },
            "snippets": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "{ searchFieldLabel: string; toggleButton: string; inputPlaceholder: string; closeButtonLabel: string; clearButtonLabel: string; }",
                    "resolved": "{ searchFieldLabel: string; toggleButton: string; inputPlaceholder: string; closeButtonLabel: string; clearButtonLabel: string; }",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Snippets for localization."
                },
                "defaultValue": "{\n        searchFieldLabel: 'Suchfeld',\n        toggleButton: 'Suche anzeigen',\n        inputPlaceholder: this.placeholder ?? 'Suchen',\n        closeButtonLabel: 'Schlie\u00DFen',\n        clearButtonLabel: 'L\u00F6schen',\n    }"
            },
            "compact": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "A flag indicating whether the search form should be displayed in a compact view.\nIf true, the search form will be displayed in a minimalistic view. Default is false."
                },
                "attribute": "compact",
                "reflect": false,
                "defaultValue": "false"
            }
        };
    }
    static get states() {
        return {
            "isLoading": {},
            "hasSearchResults": {},
            "searchResults": {},
            "isDisplaySuggestions": {}
        };
    }
    static get elementRef() { return "host"; }
    static get watchers() {
        return [{
                "propName": "searchResults",
                "methodName": "handleSearchResults"
            }, {
                "propName": "searchTerm",
                "methodName": "handleSearchTermChange"
            }];
    }
    static get listeners() {
        return [{
                "name": "resize",
                "method": "handleResize",
                "target": "window",
                "capture": false,
                "passive": true
            }, {
                "name": "click",
                "method": "handleClickDeep",
                "target": undefined,
                "capture": false,
                "passive": false
            }, {
                "name": "touchstart",
                "method": "handleTouchStart",
                "target": "document",
                "capture": false,
                "passive": true
            }, {
                "name": "keydown",
                "method": "handleKeyDown",
                "target": undefined,
                "capture": false,
                "passive": false
            }];
    }
}
//# sourceMappingURL=vx-global-search.js.map
